import { LitElement, html, css } from 'lit';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { icon } from '../../icons';
import { fireAdobeAnalyticsEvent } from '../../../../helpers/adobe-analytics.js';

// Hover delay matches the legacy material-properties toggletip, and keeps a
// pointer passing over the icon from opening (and tracking) it.
const HOVER_DELAY_MS = 100;
const VIEWPORT_MARGIN_PX = 16;
const BUBBLE_GAP_PX = 8;

// Hover, tap or keyboard toggletip. The bubble is position: fixed so a
// scrolling ancestor such as Tabby's table can't clip it, which is also why
// any scroll closes it rather than leaving it behind.
class InfoTooltip extends LitElement {
  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      css`
        :host {
          display: inline-flex;
          flex: 0 0 auto;
        }

        .trigger {
          display: inline-flex;
          padding: 0;
          border: 0;
          background: none;
          color: inherit;
          cursor: pointer;
        }

        .trigger svg {
          width: calc(16rem / var(--default-rem-px-count));
          height: calc(16rem / var(--default-rem-px-count));
        }

        .trigger .ring {
          transition: stroke 0.2s ease-in-out;
        }

        .trigger:hover .ring,
        .trigger:active .ring {
          stroke: var(--grey-dark);
        }

        .bubble {
          position: fixed;
          z-index: 100;
          box-sizing: border-box;
          width: calc(200rem / var(--default-rem-px-count));
          max-width: calc(100vw - 32px);
          padding: calc(12rem / var(--default-rem-px-count))
            calc(16rem / var(--default-rem-px-count));
          background-color: #dddddd;
          border-bottom: 2px solid #cccccc;
          color: #222222;
          font-size: var(--fs-label-small);
          line-height: var(--lh-label-small);
          letter-spacing: var(--ls-label-small);
          font-weight: var(--fw-medium);
          text-align: left;
          text-transform: none;
          white-space: normal;
          pointer-events: none;
        }
      `,
    ];
  }

  static get properties() {
    return {
      text: { type: String },
      analyticsPlacement: { type: String, attribute: 'analytics-placement' },
      analyticsLabel: { type: String, attribute: 'analytics-label' },
      _open: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this.text = '';
    this.analyticsPlacement = '';
    this.analyticsLabel = '';
    this._open = false;
    this._tracked = false;
    this._hoverTimer = null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._close();
  }

  render() {
    if (!this.text) return html``;
    return html`
      <button
        type="button"
        class="trigger"
        aria-label="More info"
        @mouseenter=${this._handleMouseEnter}
        @mouseleave=${this._close}
        @click=${this._show}
        @blur=${this._close}
      >
        ${icon('info')}
      </button>
      <span role="status">
        ${this._open
          ? html`
              <span class="bubble">${this.text}</span>
            `
          : ''}
      </span>
    `;
  }

  updated(changed) {
    if (changed.has('_open') && this._open) this._position();
  }

  _handleMouseEnter() {
    clearTimeout(this._hoverTimer);
    this._hoverTimer = setTimeout(() => this._show(), HOVER_DELAY_MS);
  }

  _show() {
    clearTimeout(this._hoverTimer);
    if (this._open) return;

    this._open = true;
    document.addEventListener('pointerdown', this._handleOutsidePointer, true);
    document.addEventListener('keydown', this._handleKeydown);
    window.addEventListener('scroll', this._close, true);
    window.addEventListener('wheel', this._close, { passive: true });
    window.addEventListener('touchmove', this._close, { passive: true });
    window.addEventListener('resize', this._close);
    this._sendAnalyticsOpenEvent();
  }

  _close = () => {
    clearTimeout(this._hoverTimer);
    if (!this._open) return;

    this._open = false;
    document.removeEventListener(
      'pointerdown',
      this._handleOutsidePointer,
      true
    );
    document.removeEventListener('keydown', this._handleKeydown);
    window.removeEventListener('scroll', this._close, true);
    window.removeEventListener('wheel', this._close);
    window.removeEventListener('touchmove', this._close);
    window.removeEventListener('resize', this._close);
  };

  _handleOutsidePointer = event => {
    if (!event.composedPath().includes(this)) this._close();
  };

  _handleKeydown = event => {
    if (event.key === 'Escape') this._close();
  };

  // Centers above the icon, flips below when there is no room, and clamps
  // to the viewport so edge columns on phones stay readable.
  _position() {
    const bubble = this.renderRoot.querySelector('.bubble');
    const trigger = this.renderRoot.querySelector('.trigger');
    if (!bubble || !trigger) return;

    const anchor = trigger.getBoundingClientRect();
    const { width, height } = bubble.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const centered = anchor.left + anchor.width / 2 - width / 2;
    const left = Math.max(
      VIEWPORT_MARGIN_PX,
      Math.min(centered, viewportWidth - VIEWPORT_MARGIN_PX - width)
    );
    const above = anchor.top - BUBBLE_GAP_PX - height;
    const top =
      above >= VIEWPORT_MARGIN_PX ? above : anchor.bottom + BUBBLE_GAP_PX;

    bubble.style.left = `${left}px`;
    bubble.style.top = `${top}px`;
  }

  // Once per tooltip per page view, whichever way it was opened.
  _sendAnalyticsOpenEvent() {
    if (this._tracked) return;

    this._tracked = true;
    fireAdobeAnalyticsEvent({
      type: 'click',
      detail: {
        eVar8: `INP|Tooltip Open|${this.analyticsPlacement}|${this.analyticsLabel}`,
      },
    });
  }
}
customElements.define('info-tooltip', InfoTooltip);
